/**
 * SignatureAuthRulesPage — Workflow · Signature Auth Rules.
 *
 * First-match-wins rules deciding how strict a signer's identity check must be
 * when a customer is sent to sign. Evaluated in priority order (lowest first);
 * the first matching rule's RequiredAuthLevel wins. A catch-all rule (trigger
 * `1 == 1`) at the bottom keeps everyone covered.
 *
 * Contract (verified against the backend 2026-06-16 — entity/DTO/service all
 * match the editor's fields; no drift):
 *   - Read:  GET /v3/config/signature-auth-rules → { rules, authLevels }
 *   - Write: POST /v3/admin/signature-auth-rules (AdminWorkflowsService upsert,
 *            natural key RuleCode)
 *   - Soft-delete: re-upsert IsActive=false (hard DELETE route exists but the
 *            UI keeps historical rows for audit).
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface RuleRow {
  ruleCode: string;
  displayName: string;
  triggerExpression: string;
  requiredAuthLevel: string;
  priority: number;
  isActive: boolean;
}

interface AuthLevel {
  code: string;
  name: string;
  description?: string | null;
  strengthRank: number;
  isActive: boolean;
}

interface EditDraft extends RuleRow {
  isNew: boolean;
}

const STARTER_EXPRESSIONS: ReadonlyArray<{ label: string; expr: string }> = [
  { label: 'In-branch with officer', expr: "session.origin == 'in-branch' && session.officerPresent == true" },
  { label: 'IRA opening (any product)', expr: "cart.primary.product.category == 'IRA'" },
  { label: 'High-value deposit', expr: 'cart.totalDeposit >= 10000' },
  { label: 'Catch-all (always)', expr: '1 == 1' },
];

function emptyDraft(defaultAuthLevel: string): EditDraft {
  return {
    ruleCode: '',
    displayName: '',
    triggerExpression: '1 == 1',
    requiredAuthLevel: defaultAuthLevel || 'email',
    priority: 100,
    isActive: true,
    isNew: true,
  };
}

export function SignatureAuthRulesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<RuleRow> | null>(null);
  const [authLevels, setAuthLevels] = useState<ReadonlyArray<AuthLevel>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RuleRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.http.get<{
          success: boolean;
          data?: { rules: ReadonlyArray<RuleRow>; authLevels?: ReadonlyArray<AuthLevel> };
          error?: string;
        }>('/v3/config/signature-auth-rules');
        if (cancelled) return;
        if (resp.success && resp.data) {
          setRows(resp.data.rules ?? []);
          setAuthLevels(resp.data.authLevels ?? []);
        } else {
          setLoadError(resp.error ?? 'Failed to load signature-auth rules.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const activeAuthLevels = useMemo(
    () => authLevels.filter((l) => l.isActive).slice().sort((a, b) => a.strengthRank - b.strengthRank),
    [authLevels],
  );

  const authLevelOptions = useMemo(
    () =>
      activeAuthLevels.length > 0
        ? activeAuthLevels.map((l) => ({ value: l.code, label: l.name }))
        : [{ value: 'email', label: 'email' }],
    [activeAuthLevels],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const sorted = (rows ?? []).slice().sort((a, b) => a.priority - b.priority);
    if (!needle) return sorted;
    return sorted.filter(
      (r) =>
        r.ruleCode.toLowerCase().includes(needle) ||
        r.displayName.toLowerCase().includes(needle) ||
        r.triggerExpression.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  const upsert = useCallback(
    async (row: RuleRow): Promise<boolean> => {
      const resp = await client.http.post<{ success: boolean; error?: string }>(
        '/v3/admin/signature-auth-rules',
        {
          headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
          body: {
            ruleCode: row.ruleCode.trim(),
            displayName: row.displayName.trim(),
            triggerExpression: row.triggerExpression.trim(),
            requiredAuthLevel: row.requiredAuthLevel,
            priority: row.priority,
            isActive: row.isActive,
          },
        },
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.ruleCode.trim() || !draft.displayName.trim() || !draft.triggerExpression.trim()) {
      setSaveError('Code, friendly name, and trigger are all required.');
      return;
    }
    if (!draft.requiredAuthLevel.trim()) {
      setSaveError('Pick a required signing strength level.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({ tone: 'info', text: `Saved. ${draft.ruleCode} is updated.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      await upsert({ ...deleteTarget, isActive: false });
      setToast({ tone: 'warn', text: `${deleteTarget.displayName} is now inactive. The runtime will skip it.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<RuleRow>> = useMemo(
    () => [
      { header: 'Priority', cell: (r) => <code className="cell-mono">{r.priority}</code> },
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.ruleCode}</code> },
      { header: 'Friendly name', cell: (r) => <span className="cell-strong">{r.displayName}</span> },
      {
        header: 'When this matches…',
        cell: (r) => <code className="cell-mono">{r.triggerExpression}</code>,
      },
      {
        header: 'Required strength',
        cell: (r) => {
          const lvl = authLevels.find((l) => l.code === r.requiredAuthLevel);
          return <Pill tone={lvl ? 'info' : 'muted'}>{lvl?.name ?? r.requiredAuthLevel}</Pill>;
        },
      },
      { header: 'Active', cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>) },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft({ ...r, isNew: false }); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [authLevels],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load signature-auth rules: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading signature-auth rules…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Signature Auth Rules — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField label="" value={search} onChange={setSearch} placeholder="code, name, or expression…" />
            </div>
            <Button
              variant="primary"
              onClick={() => { setDraft(emptyDraft(activeAuthLevels[0]?.code ?? 'email')); setSaveError(null); }}
            >
              + Add Rule
            </Button>
          </div>
        }
      >
        <p className="cell-desc">Evaluated lowest-priority-first — the first matching rule wins. Keep a catch-all (`1 == 1`) at the highest priority number.</p>
        <DataTable<RuleRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.ruleCode}
          emptyState={<p className="empty">No rules match the current search.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a signing-auth rule' : `Edit ${draft?.ruleCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Rule code"
                value={draft.ruleCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, ruleCode: v.slice(0, 32) })}
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. ira-opening"
                hint={draft.isNew ? 'Up to 32 chars. Permanent.' : 'Permanent.'}
              />
              <TextField
                label="Friendly name"
                value={draft.displayName}
                onChange={(v) => setDraft({ ...draft, displayName: v })}
                placeholder="e.g. IRA opening — KBA required"
              />
            </div>

            <div className="form-row">
              <SelectField
                label="Required strength"
                value={draft.requiredAuthLevel}
                onChange={(v) => setDraft({ ...draft, requiredAuthLevel: v })}
                options={authLevelOptions}
                hint="The signing strength this rule demands when it matches."
              />
              <NumberField
                label="Priority"
                value={draft.priority}
                onChange={(v) => setDraft({ ...draft, priority: v ?? 100 })}
                min={1}
                hint="Lower = evaluated first. Catch-all gets the highest number."
              />
            </div>

            <div>
              <span className="form-field__label">Starter expressions</span>
              <div className="token-row">
                {STARTER_EXPRESSIONS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    className="token-chip"
                    onClick={() => setDraft({ ...draft, triggerExpression: s.expr })}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <TextArea
              label="When this matches (trigger expression)"
              value={draft.triggerExpression}
              onChange={(v) => setDraft({ ...draft, triggerExpression: v })}
              rows={3}
              placeholder="cart.primary.product.category == 'IRA'"
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive rules are skipped at evaluation time."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this rule inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll set <strong>{deleteTarget.displayName}</strong> (
              <code className="cell-mono">{deleteTarget.ruleCode}</code>) to inactive. The runtime
              skips it, but the row stays for audit. Flip back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
